import { FC, memo, ReactNode } from 'react'
import s from './Favorite.module.sass'
import { middlewareAuthorized } from '@shared/lib/helpers/auth'
import { useAppDispatch, useAppSelector } from '@app/model/store/client'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { EGG } from '@shared/api/analytics'

import {
  TypeMappingOptimize1RestOutput,
  TypeMappingRestOutput,
} from '@shared/api/middleware/mappingAPI/restaurant/type'

import { PickAnalytic } from '@shared/api/analytics/types/types'
import { addFavoriteObject, deleteFavoriteObject } from '@app/model/auth/slice'

import {
  buildFavoriteSubjectId,
  FavoritePropsToStore,
  TypeCategoryFavoriteObject,
} from '@shared/api/middleware/mappingAPI/gql_favorites/type'

import { TypeMappingSelectionOutput } from '@shared/api/middleware/mappingAPI/selection/type'
import { TypeMappingNewsOutput } from '@shared/api/middleware/mappingAPI/news/type'
import type { ActivityType } from '@widgets/Restaurant/RestConcerts/helpers'
import { addTemporaryNotice } from '@widgets/Common/Notices/model/actions'
import { MAPPING_API_GQL } from '@shared/api/middleware/mappingAPI'
import { GQL_TAGS } from '@shared/api/api.gql'
import { LightGQL } from '@shared/api/gql-service/LightGQLService'
import { DATA_PREVENT_ROUTE_PENDING } from '@shared/lib/constants/dataAttributes'

import type {
  AddFavoriteV3Mutation,
  AddFavoriteV3MutationVariables,
  DeleteFavoriteV3Mutation,
  DeleteFavoriteV3MutationVariables,
} from '@shared/api/types/graphql/generated-afisha'

type AnalyticFavoriteCallback = (
  data: unknown,
  other: PickAnalytic<
    'section_name',
    'section_index' | 'model_type' | 'provider' | 'object_id' | 'object_name' | 'object_type' | 'city_id'
  >,
) => void

type FavoriteAnalyticData =
  | TypeMappingRestOutput
  | TypeMappingOptimize1RestOutput
  | TypeMappingSelectionOutput
  | TypeMappingNewsOutput
  | ActivityType

type FavoriteAnalytic = {
  data: FavoriteAnalyticData
  other: PickAnalytic<
    'section_name',
    'section_index' | 'model_type' | 'provider' | 'object_id' | 'object_name' | 'object_type' | 'city_id'
  >
}

interface Props {
  category: TypeCategoryFavoriteObject
  id: number
  subjectId?: string
  children: ReactNode
  analytic?: FavoriteAnalytic
  className?: string
}

const FavoriteContainer: FC<Props> = ({ children, id, category, subjectId, ...props }) => {
  const active = useAppSelector((state) => {
    const groupName = FavoritePropsToStore[category]
    const favoritesGroup = state.auth.user.data?.favorites_objects[groupName]
    return favoritesGroup ? id in favoritesGroup : false
  })

  if (category === 'selection' && !process.env.IS_SHOW_FAVORITE_BUTTON_SELECTION) return
  if (category === 'news' && !process.env.SHOW_FAVORITE_BUTTON_NEWS) return

  return (
    <Favorite
      active={active}
      id={id}
      category={category}
      subjectId={subjectId}
      analytic={props.analytic}
      className={props.className}>
      {children}
    </Favorite>
  )
}

interface PropsFavorite {
  active: boolean
  category: TypeCategoryFavoriteObject
  id: number
  subjectId?: string
  children: ReactNode
  analytic?: FavoriteAnalytic
  className?: string
}

const Favorite: FC<PropsFavorite> = ({ active, category, id, subjectId, analytic, children, className = '' }) => {
  const dispatch = useAppDispatch()

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    const getAnalyticMethod = () => {
      switch (category) {
        case 'restaurant':
          return active ? EGG.entity.restaurant.click_remove_favorite : EGG.entity.restaurant.click_add_favorite
        case 'selection':
          return active ? EGG.entity.selection.click_remove_favorite : EGG.entity.selection.click_add_favorite
        case 'concert':
        case 'exhibition':
        case 'movie':
        case 'performance':
        case 'sport':
          if (active || analytic?.other.section_name !== 'gigachat') return null
          return EGG.gigachat.events_add_to_fav
        case 'news':
          return active ? EGG.entity.news.click_remove_favorite : EGG.entity.news.click_add_favorite
        default:
          return null
      }
    }

    const analyticMethod = getAnalyticMethod()

    const resolvedSubjectId = buildFavoriteSubjectId(category, id, subjectId)

    middlewareAuthorized(() => {
      if (!resolvedSubjectId) {
        dispatch(addTemporaryNotice({ markup: 'Что-то пошло не так, попробуйте позже' }))
        return
      }

      const actionMethod = active ? deleteFavoriteObject : addFavoriteObject
      const revertActionMethod = active ? addFavoriteObject : deleteFavoriteObject
      dispatch(actionMethod({ object: category, id: id }))

      LightGQL.afisha
        .query<
          AddFavoriteV3Mutation | DeleteFavoriteV3Mutation,
          AddFavoriteV3MutationVariables | DeleteFavoriteV3MutationVariables
        >(active ? GQL_TAGS.DELETE_FAVORITE : GQL_TAGS.ADD_FAVORITE, {
          variables: {
            input: {
              subjectId: resolvedSubjectId,
              clientMutationId: 'AfishaRestaurantsForever',
            },
          },
          requestInit: { credentials: 'include' },
          mapping: (data) =>
            (active ? MAPPING_API_GQL.deleteFavorite(data) : MAPPING_API_GQL.addFavorite(data)) as
              AddFavoriteV3Mutation | DeleteFavoriteV3Mutation,
        })
        .then(() => {
          dispatch(addTemporaryNotice({ markup: active ? 'Удалено из избранного' : 'Добавлено в избранное' }))
          if (analytic) {
            const callAnalytic = analyticMethod as AnalyticFavoriteCallback | null
            callAnalytic?.(analytic.data, analytic.other)
          }
        })
        .catch(() => {
          dispatch(addTemporaryNotice({ markup: 'Что-то пошло не так, попробуйте позже' }))
          dispatch(revertActionMethod({ object: category, id: id }))
        })
    })
  }

  return (
    <BlockBoundary>
      <div
        className={`${s.button} ${className}`}
        onClick={handleClick}
        data-active={active}
        {...{ [DATA_PREVENT_ROUTE_PENDING]: true }}>
        {children}
      </div>
    </BlockBoundary>
  )
}

export default memo(FavoriteContainer)
